import React, { useContext } from "react";

import { useTranslation } from "react-i18next";

import { EmptyContent } from "@features/offer/desktop/event";
import { EventStatusLegend } from "@features/offer/shared/event/EventStatusLegend";
import {
	BetBuilderPicksEventPopup,
	BetBuilderPicksEventSlider,
} from "@features/quick-play/desktop/bet-builder-picks";
import BettingTipsPopup from "@features/quick-play/desktop/betting-tips/BettingTipsPopup";
import BettingTipsEventList from "@features/quick-play/desktop/betting-tips/components/BettingTipsEventList";
import {
	EventContext,
	OfferOptionsContext,
	OfferStoreContext,
} from "@gp/components";
import EventViewStore from "@state/stores/offer/event/EventViewStore";
import { AdditionalOfferViewStore } from "@state/stores/offer/shared/AdditionalOfferViewStore";
import { THEME_SETTINGS } from "@theme-settings";
import { observer } from "mobx-react";

import { BettingTypeGroupHeader, BettingTypeGroups } from ".";
import { BetBoostersSubHeader } from "./BetBoostersSubHeader";

export const GroupedBettingTypes = observer(function GroupedBettingTypes() {
	const event = useContext(EventContext);
	const store = useContext(OfferStoreContext) as
		| EventViewStore
		| AdditionalOfferViewStore;
	const offerOptionsContext = useContext(OfferOptionsContext);
	const { t } = useTranslation();
	const bettingTypeGroups = store.bettingTypeGroups;
	const isBettingTipsFeatureEnabled =
		THEME_SETTINGS.features.quickPlayOptions?.includes("betting-tips");

	const isBetBuilderFeatureEnabled =
		THEME_SETTINGS.features.quickPlayOptions?.includes("bet-builder-picks");

	const isBetBuilderAvailable =
		(store?.betBoosterEventPicks?.betBuilderPicksStore?.betBuilderPresetIds
			?.length ?? 0) > 0;

	const isBettingTipsAvailable =
		store.bettingTipsStore.bettingTips?.length > 0;

	if (
		store.activeSubGroup == "bet-builder" &&
		!isBetBuilderAvailable &&
		isBettingTipsAvailable
	) {
		store.setActiveSubGroup("betting-tips");
	}

	if (
		store.activeSubGroup == "betting-tips" &&
		!isBettingTipsAvailable &&
		isBetBuilderAvailable
	) {
		store.setActiveSubGroup("bet-builder");
	}

	if (
		store.displayBettingGroup == "bet-builder" &&
		!store.event?.betBuilder
	) {
		store.setDisplayBettingGroup("all");
	}

	if (
		store.displayBettingGroup !== "bet-builder" &&
		store.displayBettingGroup !== "favorites" &&
		bettingTypeGroups.find(
			(g: { abrv: string }) => g.abrv === store.displayBettingGroup
		) == null &&
		store.displayBettingGroup != "all"
	) {
		store.setDisplayBettingGroup("all");
	}

	if (
		store.displayBettingGroup == "favorites" &&
		store.favoritesBettingTypeGroups?.[event.id] &&
		// @ts-expect-error
		event?.favoritesBettingTypesIds?.length == 0
	) {
		store.setDisplayBettingGroup("all");
	}

	const eventOfferKeys = store.eventKeysMap.get(event.id);

	if (
		bettingTypeGroups.length === 0 ||
		eventOfferKeys == null ||
		eventOfferKeys.size === 0
	) {
		return <EmptyContent message={t("BETTING_TYPE.NO_OFFER")} />;
	}

	return (
		<>
			{store.betBoosterEventPicks.betBuilderPicksStore && (
				<BetBuilderPicksEventPopup />
			)}

			<BettingTipsPopup
				bettingTipsStore={store.bettingTipsStore}
				isEventLinkDisabled={true}
			/>

			<div className="event__offer">
				<div className="event__play">
					{store.displayBettingGroup === "all" &&
						isBetBuilderFeatureEnabled &&
						isBetBuilderAvailable &&
						isBettingTipsFeatureEnabled &&
						isBettingTipsAvailable && <BetBoostersSubHeader />}

					{store.displayBettingGroup === "all" &&
						store.activeSubGroup === "betting-tips" && (
							<BettingTipsEventList />
						)}

					{isBetBuilderFeatureEnabled &&
						(store.displayBettingGroup === "bet-builder" ||
							(store.displayBettingGroup === "all" &&
								store.activeSubGroup === "bet-builder")) && (
							<BetBuilderPicksEventSlider />
						)}
				</div>

				<BettingTypeGroupHeader />
				{/* @ts-expect-error */}
				{offerOptionsContext.isOfferHistory && <EventStatusLegend />}
				<BettingTypeGroups />
			</div>
		</>
	);
});
